import { useEffect, useState } from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import MessagesPanel from "@/components/MessagesPanel";
import MessagesShell from "@/components/MessagesShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Send, ListChecks, MessageSquare, User2, ArrowRight, X, Check, Clock } from "lucide-react";

const NAV = [
  { to: "/surgeon", end: true, label: "Request anesthetist", icon: Send },
  { to: "/surgeon/requests", label: "My requests", icon: ListChecks },
  { to: "/surgeon/messages", label: "Messages", icon: MessageSquare },
  { to: "/surgeon/profile", label: "Profile", icon: User2 },
];

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  accepted: "bg-green-50 text-green-700 border-green-200",
  declined: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function SurgeonDashboard() {
  return (
    <AppShell items={NAV}>
      <Routes>
        <Route index element={<NewRequest />} />
        <Route path="requests" element={<MyRequests />} />
        <Route path="messages" element={<MessagesShell><MessagesPanel contactsEndpoint="/surgeon/contacts" /></MessagesShell>} />
        <Route path="profile" element={<ProfileTab />} />
        <Route path="*" element={<Navigate to="/surgeon" replace />} />
      </Routes>
    </AppShell>
  );
}

function Field({ label, children, colSpan = 1 }) {
  return <div className={colSpan === 2 ? "col-span-2" : ""}><Label className="field-label">{label}</Label>{children}</div>;
}

function NewRequest() {
  const { user } = useAuth();
  const [anesthetists, setAnesthetists] = useState([]);
  const [form, setForm] = useState({
    target_anesthetist_id: "any",
    patient_name: "",
    date: "",
    start_time: "",
    end_time: "",
    procedure_type: "",
    location: "",
    notes: "",
    urgency: "elective",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/surgeon/anesthetists").then((r) => setAnesthetists(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_name || !form.date || !form.start_time || !form.procedure_type) {
      toast.error("Please fill patient, date, start time, and procedure type");
      return;
    }
    setBusy(true);
    try {
      await api.post("/surgeon/requests", {
        ...form,
        target_anesthetist_id: form.target_anesthetist_id === "any" ? "" : form.target_anesthetist_id,
      });
      toast.success("Request sent");
      setForm({ target_anesthetist_id: "any", patient_name: "", date: "", start_time: "", end_time: "", procedure_type: "", location: "", notes: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="surgeon-new-request">
      <div>
        <p className="aamg-label">Coverage request</p>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Welcome, Dr. {user?.name?.split(" ").slice(-1)[0]}</h1>
        <p className="text-slate-500 mt-1">Send an anesthesia coverage request to a specific anesthetist or to anyone available.</p>
      </div>

      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">New procedure request</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            <Field label="Urgency" colSpan={2}>
              <div className="flex gap-2" data-testid="urgency-toggle">
                {[
                  { v: "elective", label: "Elective", desc: "Standard scheduling" },
                  { v: "emergent", label: "⚡ Emergent", desc: "Immediate coverage" },
                ].map((u) => (
                  <button key={u.v} type="button" onClick={() => setForm({ ...form, urgency: u.v })} data-testid={`urgency-${u.v}`}
                    className={`flex-1 px-4 py-3 rounded-md border text-left transition-colors ${form.urgency === u.v ? (u.v === "emergent" ? "bg-red-50 border-red-300 text-red-900" : "bg-[#339999]/5 border-[#339999]/40 text-[#339999]") : "bg-white border-slate-200 hover:border-slate-400"}`}>
                    <div className="text-sm font-semibold">{u.label}</div>
                    <div className="text-xs text-slate-500">{u.desc}</div>
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Anesthetist" colSpan={2}>
              <Select value={form.target_anesthetist_id} onValueChange={(v) => setForm({ ...form, target_anesthetist_id: v })}>
                <SelectTrigger data-testid="req-anesthetist-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any" data-testid="req-anesthetist-any">Any available anesthetist</SelectItem>
                  {anesthetists.map((a) => (
                    <SelectItem key={a.user_id} value={a.user_id} data-testid={`req-anesth-${a.user_id}`}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">
                {form.target_anesthetist_id === "any"
                  ? "All anesthetists will see your request — the first to accept gets it."
                  : "Only the selected anesthetist will see your request."}
              </p>
            </Field>
            <Field label="Patient name"><Input value={form.patient_name} onChange={set("patient_name")} data-testid="req-patient" required /></Field>
            <Field label="Procedure type"><Input value={form.procedure_type} onChange={set("procedure_type")} data-testid="req-procedure" placeholder="e.g. Laparoscopic cholecystectomy" required /></Field>
            <Field label="Date"><Input type="date" value={form.date} onChange={set("date")} data-testid="req-date" required /></Field>
            <Field label="Location"><Input value={form.location} onChange={set("location")} placeholder="Hospital / centre" /></Field>
            <Field label="Start time"><Input type="time" value={form.start_time} onChange={set("start_time")} data-testid="req-start-time" required /></Field>
            <Field label="End time"><Input type="time" value={form.end_time} onChange={set("end_time")} /></Field>
            <Field label="Notes" colSpan={2}><Textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="Anesthesia plan, patient considerations, ASA class, allergies, etc." /></Field>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={busy} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="req-submit-btn">
                {busy ? "Sending…" : "Send request"} <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="aamg-card">
        <CardContent className="p-5 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-slate-900">Need to review past requests?</div>
            <div className="text-xs text-slate-500">See status, accepted anesthetist, and cancellations.</div>
          </div>
          <Link to="/surgeon/requests"><Button variant="outline" size="sm">Open my requests</Button></Link>
        </CardContent>
      </Card>
    </div>
  );
}

function MyRequests() {
  const [items, setItems] = useState([]);
  const refresh = () => api.get("/surgeon/requests").then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); const iv = setInterval(refresh, 15000); return () => clearInterval(iv); }, []);

  const cancel = async (rid) => {
    try { await api.delete(`/surgeon/requests/${rid}`); toast.success("Request cancelled"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot cancel"); }
  };

  return (
    <div className="space-y-5" data-testid="surgeon-my-requests">
      <div className="flex items-center justify-between">
        <div>
          <p className="aamg-label">History</p>
          <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>My requests</h1>
        </div>
        <Link to="/surgeon"><Button className="bg-[#339999] hover:bg-[#2A7F7F]"><Send className="h-4 w-4 mr-2" />New</Button></Link>
      </div>
      <Card className="aamg-card">
        <CardContent className="p-0 overflow-x-auto">
          {items.length === 0 ? (
            <div className="p-12 text-center text-slate-400">No requests yet — start with a new one.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((r) => (
                <li key={r.request_id} className="p-4 flex items-center justify-between gap-4" data-testid={`request-${r.request_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{r.procedure_type}</span>
                      <Badge className={`border capitalize ${STATUS_BADGE[r.status] || STATUS_BADGE.pending}`}>{r.status}</Badge>
                      {!r.target_anesthetist_id && r.status === "pending" && (
                        <Badge className="border bg-blue-50 text-blue-700 border-blue-200">Any anesthetist</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {r.patient_name} · {r.date} {r.start_time}{r.end_time && `–${r.end_time}`} · {r.location || "—"}
                    </div>
                    {r.accepted_by_name && (
                      <div className="text-xs text-green-700 mt-0.5 flex items-center gap-1"><Check className="h-3 w-3" /> Accepted by {r.accepted_by_name}</div>
                    )}
                    {r.status === "declined" && r.decline_reason && (
                      <div className="text-xs text-red-700 mt-0.5">Declined — {r.decline_reason}</div>
                    )}
                    {r.notes && <div className="text-xs text-slate-500 mt-1 italic line-clamp-2">"{r.notes}"</div>}
                  </div>
                  {r.status === "pending" && (
                    <Button size="sm" variant="outline" className="text-red-600 shrink-0" onClick={() => cancel(r.request_id)} data-testid={`cancel-${r.request_id}`}>
                      <X className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ProfileTab() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", bio: user?.bio || "" });
  const [pw, setPw] = useState("");
  const save = async () => { await api.put("/surgeon/profile", form); toast.success("Profile updated"); refresh(); };
  const changePw = async () => {
    if (pw.length < 6) { toast.error("Min 6 chars"); return; }
    await api.put("/surgeon/password", { new_password: pw }); toast.success("Password updated"); setPw("");
  };
  return (
    <div className="space-y-5 max-w-2xl" data-testid="surgeon-profile">
      <div><p className="aamg-label">Account</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Profile</h1></div>
      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">Personal information</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          <Field label="Full name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Bio / specialty" colSpan={2}><Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="e.g. General surgery — laparoscopic & open" /></Field>
          <div className="sm:col-span-2"><Button onClick={save} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="profile-save-btn">Save changes</Button></div>
        </CardContent>
      </Card>
      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">Change password</CardTitle></CardHeader>
        <CardContent className="flex gap-2 items-end">
          <Field label="New password"><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
          <Button onClick={changePw} className="bg-[#339999] hover:bg-[#2A7F7F]">Update</Button>
        </CardContent>
      </Card>
    </div>
  );
}
